"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { X, History, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { OptimizedImage } from "@/components/ui/optimized-image";
import type { Brand } from "@/lib/types";

const STORAGE_KEY = "whobrands_recently_viewed";
const MAX_ITEMS = 8;

interface StoredItem {
  slug: string;
  name: string;
  logo?: string;
  companySlug?: string;
  companyName?: string;
  categorySlug?: string;
  viewedAt: number;
}

export function trackBrandView(
  brand: Pick<Brand, "slug" | "name" | "logo" | "companySlug" | "categorySlug">,
  company?: { name: string }
) {
  if (typeof window === "undefined") return;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const items: StoredItem[] = stored ? JSON.parse(stored) : [];

    const filtered = items.filter((item) => item.slug !== brand.slug);

    const newItem: StoredItem = {
      slug: brand.slug,
      name: brand.name,
      logo: brand.logo,
      companySlug: brand.companySlug,
      companyName: company?.name,
      categorySlug: brand.categorySlug,
      viewedAt: Date.now(),
    };

    const updated = [newItem, ...filtered].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event("whobrands:recently-viewed-changed"));
  } catch {
    // localStorage might be unavailable (private mode, etc.)
  }
}

function formatCategoryLabel(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function RecentlyViewed() {
  const [items, setItems] = useState<StoredItem[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const load = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        setItems(stored ? JSON.parse(stored) : []);
      } catch {
        setItems([]);
      }
    };
    load();
    window.addEventListener("whobrands:recently-viewed-changed", load);
    return () => window.removeEventListener("whobrands:recently-viewed-changed", load);
  }, []);

  const handleRemove = (slug: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const current: StoredItem[] = stored ? JSON.parse(stored) : [];
      const updated = current.filter((item) => item.slug !== slug);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event("whobrands:recently-viewed-changed"));
    } catch {
      // ignore
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      localStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new Event("whobrands:recently-viewed-changed"));
    } catch {
      // ignore
    }
  };

  if (!mounted || items.length === 0) return null;

  return (
    <section className="py-8 md:py-10 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-serif font-semibold text-foreground flex items-center gap-2">
            <History className="h-5 w-5 text-accent" />
            Recently Viewed
          </h2>
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear all
          </button>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
          {items.map((item) => (
            <Link
              key={item.slug}
              href={`/brands/${item.slug}`}
              className="group flex-shrink-0 w-52 snap-start"
            >
              <Card className="h-full hover:shadow-lg hover:-translate-y-1 hover:border-accent/30 transition-all border-border p-0 overflow-hidden">
                <div className="relative h-28 w-full bg-gradient-to-br from-muted to-background dark:from-white/35 dark:to-white/35 overflow-hidden rounded-t-lg">
                  {item.logo && (
                    <OptimizedImage
                      src={item.logo}
                      alt={item.name}
                      fallbackSrc="/placeholder.svg"
                      fill
                      className="object-contain"
                      sizes="208px"
                    />
                  )}
                  {item.categorySlug && (
                    <span className="absolute top-2 left-2 text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground px-2 py-1 rounded-md border border-border/80 shadow-sm">
                      {formatCategoryLabel(item.categorySlug)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleRemove(item.slug, e)}
                    className="absolute top-2 right-2 h-6 w-6 rounded-full bg-background/90 backdrop-blur-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors border border-border/80 shadow-sm"
                    aria-label={`Remove ${item.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base text-foreground group-hover:text-accent transition-colors truncate">
                        {item.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-0.5 truncate">
                        {item.companyName || "Unknown"}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
